// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import getFullBreakpointValues from "@toolkit/Framework/Common/Utilities/getFullBreakpointValues";
import { getGridPreset } from "@toolkit/Framework/Common/Utilities/gridPreset";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css, useTheme } from "styled-components";

export type LayoutOption = DataOption<GridItemProps["width"]>;
export interface LayoutOptionMapper {
    custom: LayoutOption;
    fourColumn: LayoutOption;
    threeColumn: LayoutOption;
}
export type LayoutOptions = DataOptions<LayoutOption> & LayoutOptionMapper;
export type Layout = keyof LayoutOptionMapper;

export const layoutOptions: LayoutOptions = {
    threeColumn: { id: "threeColumn", displayName: "3 Columns", value: [12, 6, 4, 4, 4], isDefault: true },
    fourColumn: { id: "fourColumn", displayName: "4 Columns", value: [12, 6, 4, 4, 3] },
    custom: { id: "custom", displayName: "Custom" },
};

const enum fields {
    customCSS = "customCSS",
    customLayout = "customLayout",
    gap = "gap",
    layout = "layout",
    maxWidth = "maxWidth",
    tiles = "tiles",
}

const enum tileFields {
    id = "id",
    title = "title",
}

const customLayoutField = getNumberTextField(fields.customLayout, { min: 1, max: 12 });
const gapField = getCssUnitField(fields.gap, { shorthands: ["row", "column"] });
const maxWidthField = getCssUnitField(fields.maxWidth);

export interface TileGridTileProps {
    fields: {
        [tileFields.id]: string;
        [tileFields.title]: string;
    };
}

export interface TileGridProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.customLayout]: string;
        [fields.gap]: string;
        [fields.layout]: string;
        [fields.maxWidth]: string;
        [fields.tiles]: TileGridTileProps[];
    };
}

export interface TileGridStyles {
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

const tileGridStyles: TileGridStyles = {
    gridItem: {
        css: css`
            justify-content: space-between;
        `,
    },
};

const styles = tileGridStyles;

const TileGrid: FC<TileGridProps> = ({ fields: { customCSS, customLayout, gap, layout, maxWidth, tiles }, id }) => {
    const theme = useTheme();

    const gridContainerStyles = useMemo(() => {
        const gridPreset = gapField.parseToGridPreset(gap);

        return mergeProps<TileGridStyles["gridContainer"]>(
            styles.gridContainer,
            mergeProps<TileGridStyles["gridContainer"]>(gridPreset ? getGridPreset(gridPreset) : undefined, {
                gap: 0,
                css: css`
                    ${breakpointValues(maxWidthField.parse(maxWidth), "max-width")}
                    ${customCSS}
                `,
            }),
        );
    }, [customCSS, gap, maxWidth]);

    const gridItemStyles = useMemo(() => {
        return mergeProps<TileGridStyles["gridItem"]>(styles.gridItem, {
            width:
                (layout === layoutOptions.custom.id
                    ? (getFullBreakpointValues(
                          customLayoutField.parse(customLayout),
                          theme.breakpoints.keys.length,
                      ) as GridItemProps["width"])
                    : getDataOptionValue(layoutOptions, layout)) || 12,
        });
    }, [customLayout, layout]);

    if (!tiles?.length) {
        return null;
    }

    return (
        <GridContainer {...gridContainerStyles}>
            {tiles.map(({ fields: tile }, index) => {
                return (
                    <GridItem key={tile.id || index} {...gridItemStyles} className="tile">
                        <Zone contentId={id} zoneName={`Tile-${tile.id || index}`} />
                    </GridItem>
                );
            })}
        </GridContainer>
    );
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: TileGrid,
    definition: {
        group: "Basic",
        icon: "border-all",
        displayName: "Tile Grid",
        fieldDefinitions: [
            {
                ...getDataOptionsFieldDefinition(layoutOptions),
                name: fields.layout,
                fieldType: "Contextual",
                hideEmptyOption: true,
            },
            {
                ...customLayoutField.definition,
                fieldType: "Contextual",
                isVisible: x => x.fields.layout === layoutOptions.custom.id,
            },
            {
                ...gapField.definition,
                displayName: "Gap Between Tiles",
                fieldType: "Contextual",
            },
            {
                name: fields.tiles,
                editorTemplate: "ListField",
                getDisplay: x => x.fields.title || translate("Tile"),
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: tileFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: tileFields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                ...maxWidthField.definition,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
